<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="stockHistoryTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="stockHistoryTitle">
            Verlauf {{ device?.stock_number ?? '' }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="spinner" aria-label="Lädt"></div>
          <div v-else-if="loadError" class="error">{{ loadError }}</div>
          <div v-else-if="entries.length === 0" class="history-empty">
            Zu diesem Gerät ist noch nichts festgehalten.
          </div>
          <ol v-else class="history-list">
            <li v-for="entry in entries" :key="entry.id" class="history-entry">
              <div class="history-meta">
                {{ formatDate(entry.creation_timestamp) }} · {{ entry.user_name }}
              </div>
              <div class="history-note">{{ entry.note || '—' }}</div>
            </li>
          </ol>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Schließen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { listHistory, ApiError, type StockDevice, type StockDeviceHistoryEntry } from '../services/stockDevices';

/**
 * Verlauf eines Lagergeräts: Einbuchung, Korrektur, Versand, Storno, Lieferschein.
 *
 * Der Verlauf hängt am Gerät, nicht am Ticket — mit dem Ticketabschluss sind
 * Vorgang und Bestand getrennt. Geladen wird bei jedem Öffnen neu; es sind wenige
 * Zeilen je Gerät, und ein veralteter Verlauf wäre irreführend.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;

const device    = ref<StockDevice | null>(null);
const entries   = ref<StockDeviceHistoryEntry[]>([]);
const loading   = ref(false);
const loadError = ref<string | null>(null);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
  }
});

onBeforeUnmount(() => {
  modal?.dispose();
  modal = null;
});

async function open(item: StockDevice): Promise<void> {
  device.value    = item;
  entries.value   = [];
  loadError.value = null;
  loading.value   = true;
  modal?.show();
  try {
    entries.value = await listHistory(item.id);
  } catch (e) {
    loadError.value = e instanceof ApiError ? e.message : 'Der Verlauf konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

defineExpose({ open });
</script>

<style scoped>
.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.history-entry {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f3f5;
}

.history-entry:last-child {
  border-bottom: none;
}

.history-meta {
  font-size: 0.82rem;
  color: #6c757d;
}

.history-note {
  white-space: pre-line;
}

.history-empty {
  color: #6c757d;
}

.error {
  color: #dc3545;
}
</style>
